// Third-party
import Head from 'next/head';
import dynamic from 'next/dynamic';
import React, { useState, useRef, useEffect } from 'react';

// First-party
import { Slot, If, For, createPage } from '@lib/util/templateHelpers';
import rating from '@lib/util/rating';
// Component lib
import shared from '@lib/components';
import Meta from '@lib/components/head/Meta';

const { 
  Block,
  Box,
  RouterLink,
  Content,
  Title,
  DocumentTitle
} = shared;

// Local
import styles from './index.module.scss';
import gameApi from '@/apis/drupal/game';
import GameTileLayout from '@/layouts/GameTileLayout';

// Local components
import CarouselBlock from '@/components/CarouselBlock';
import IoGameTile from '@/components/IoGameTile';
const Carousel = dynamic(() => import('@/components/Carousel'));
const AllGamesBlock = dynamic(() => import('@/components/AllGamesBlock'));

// Mirrors the cards rendered on the iogames-pages CF Pages site at /category/casino.
// Updates here must be paired with whoever maintains that page so the two stay in sync.
const GAMES_OF_CHANCE_CARDS = [
  {
    id: 'casino-crash-gambling-sites',
    path: 'https://iogames.space/en/crash-gambling-sites',
    title: 'Crash Gambling Sites',
    thumbnailUrl: 'https://iogames.space/images/crash-gambling/hero.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 4,
  },
  {
    id: 'casino-real-money-online-casinos',
    path: 'https://iogames.space/en/real-money-online-casinos',
    title: 'Best Online Casinos',
    thumbnailUrl: 'https://iogames.space/images/real-money-casinos/hero.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 4,
  },
  {
    id: 'casino-online-casinos-florida',
    path: 'https://iogames.space/en/online-casinos-florida',
    title: 'Online Casinos Florida',
    thumbnailUrl: 'https://iogames.space/images/florida-casinos/hero.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 4,
  },
  {
    id: 'casino-best-online-slots',
    path: 'https://iogames.space/en/best-online-slots',
    title: 'Best Online Slots',
    thumbnailUrl: 'https://iogames.space/images/best-online-slots/hero.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 4,
  },
];

// Mirrors the cards rendered on the iogames-pages CF Pages site at /category/free-slots.
// Updates here must be paired with whoever maintains that page so the two stay in sync.
const SLOT_GAMES_CARDS = [
  {
    id: 'free-slots-mega-moolah',
    path: 'https://iogames.space/slots/mega-moolah',
    title: 'Mega Moolah',
    thumbnailUrl: 'https://iogames.space/images/slots/mega-moolah.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 5,
  },
  {
    id: 'free-slots-a-night-with-cleo',
    path: 'https://iogames.space/slots/a-night-with-cleo',
    title: 'A Night With Cleo',
    thumbnailUrl: 'https://iogames.space/images/slots/a-night-with-cleo.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 5,
  },
  {
    id: 'free-slots-cash-bandits-3',
    path: 'https://iogames.space/slots/cash-bandits-3',
    title: 'Cash Bandits 3',
    thumbnailUrl: 'https://iogames.space/images/slots/cash-bandits-3-gameplay.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 5,
  },
  {
    id: 'free-slots-immortal-romance',
    path: 'https://iogames.space/slots/immortal-romance',
    title: 'Immortal Romance',
    thumbnailUrl: 'https://iogames.space/images/slots/immortal-romance.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 5,
  },
  {
    id: 'free-slots-starburst',
    path: 'https://iogames.space/slots/starburst',
    title: 'Starburst',
    thumbnailUrl: 'https://iogames.space/images/slots/starburst.jpg',
    supportsMobile: true,
    hideRating: true,
    totalCount: 5,
  },
];

const GameCarousel = (props) => {
  const [ games, setGames ] = useState(Array.isArray(props.games) ? props.games : []);
  const [ gamesFormatted, setGamesFormatted ] = useState([]);

  useEffect(() => {
    if (!(props.featured && props.stickiedGames)) return;

    const formatted = [];

    let i = 0;
    let gameIndex = 0;
    let stickyIndex = 0;

    while (gameIndex < games.length) {
      // Featured game (big)
      if (i % 5 === 0) {
		const game = games[ gameIndex++ ];
		game.isFeatSpot = true;
        formatted.push(game);
      }

      // Stickied game (small)
      else {
		const game = props.stickiedGames[ stickyIndex++ % props.stickiedGames.length ];
		game.isFeatSpot = false;
        formatted.push(game);
      }

      i++;
    }

    setGamesFormatted(formatted);

  }, [ games ]);

  const fetchMoreGames = async (nextLimit, nextIndex) => {
    return await props.fetchMoreGames(nextLimit, nextIndex);
  };

  const setNextGames = async (nextGames) => {
    const filteredGames = nextGames.filter((game) => {
      return !games.find((g) => (g.path === game.path));
    });
    setGames((currentGames) => currentGames.concat(props.featured ? nextGames : filteredGames));
  };

  const transition = {
    easing: 'spring(1, 100, 20, 0)'
  };
  
  const totalCount = props.games && props.games.length 
    ? props.games[0].totalCount + (
        props.stickiedGames && props.stickiedGames[0]
          ? props.stickiedGames[0].totalCount
          : 0
      )
    : 0;

  const hasFetchedMoreOnLoadRef = useRef(false);

  useEffect(() => {
    if (!props.fetchMoreOnLoad) return;
    if (hasFetchedMoreOnLoadRef.current) return;

    hasFetchedMoreOnLoadRef.current = true;

    const fetchMore = async () => {
      const tileWidth = 178 + 8; // width + gap
      const numTilesToLoad = Math.max(
        Math.ceil(window.innerWidth / tileWidth) - (props.games && props.games.length),
        4
      );
      const nextGames = await props.fetchMoreGames(numTilesToLoad, props.games.length);
      const showNextGames = Array.isArray(nextGames) && nextGames.length > 0 ? nextGames : [];
      setGames(prevGames => [ ...(Array.isArray(prevGames) ? prevGames : []), ...showNextGames ]);
    };
    fetchMore();
  }, [ props.fetchMoreOnLoad ])

  return (
    <Carousel
      initialIndex={props.games && props.games.length}
      fetchMoreGames={fetchMoreGames}
      setNextGames={setNextGames}
      totalGames={totalCount}
      transition={transition}
      featured={props.featured}
    >
      {
        If(props.featured && props.stickiedGames, () => (
          <>
            {
              For(gamesFormatted, (game, index) => (
                <IoGameTile
                  key={`${index}__${game.id || 'lastGame'}`}
                  game={game}
                  className={index < props.games.length && '--finished'}
                  featured={index % 5 === 0}
                  priority={props.priority && index <= 8}
                />
              ))
            }
          </>
        ))
        .Else(() => (
          <>
            {
              Array.isArray(games) && For(games, (game, index) => (
                <IoGameTile
                  key={`${index}__${game.id || 'lastGame'}`}
                  game={game}
                  className={index < props.games.length && '--finished'}
                  priority={props.priority && index <= 8}
                />
              ))
            }
          </>
        ))
        .EndIf()
      }
    </Carousel>
  );
}

export async function getServerSideProps () {
  // createNetworkApi resolves failed calls to a non-iterable { error } object.
  // Coerce every list to an array so one failed Drupal call degrades a single
  // carousel to an empty row instead of crashing the whole homepage on hydrate.
  const asList = (r) => (Array.isArray(r) ? r : []);
  const featuredGames = asList(await gameApi.listFeaturedOrSticky(2));
  const stickiedGames = asList(await gameApi.listSticky(100));
  const newGames = asList(await gameApi.listNew(4));
  const popularGames = asList(await gameApi.listPopularAlt(4));

  return {
    props: {
      featuredGames,
      stickiedGames,
      newGames,
      popularGames,
    },
  }
}

export default createPage('IndexPage', { styles }, function IndexPage ({ className }, props) {
  const [ categories, setCategories ] = useState({});
  const [ allGames, setAllGames ] = useState(null);
  const carouselRef = useRef(null);
  const fetchingAllGamesRef = useRef(false);

  useEffect(() => {
    const fetchCategory = async (id, el) => {
      const category = process.env.APP_CONFIG.HOME_PAGE_CATEGORY_CAROUSELS.find((cat) => cat.id === id);

      setCategories((curCategories) => { 
        return {
          ...curCategories,
          [id]: {
            ...category
          },
        }
      });
    };

    const fetchAllGames = async () => {
      if (fetchingAllGamesRef.current) return;

      fetchingAllGamesRef.current = true;

      const nextGames = await gameApi.listAll(12);
      setAllGames(nextGames);
    };

    const handler = () => {
      const currentY = window.scrollY + window.innerHeight;
      const currentHeight = window.innerHeight;

      Array.from(carouselRef.current.querySelectorAll('.CarouselBlock')).forEach((el) => {
        const id = el.getAttribute('data-id');
        const ready = el.getAttribute('data-ready') === 'true';

        if (ready) return;

        const brect = el.getBoundingClientRect();
        const targetY = brect.y;

        if (currentY >= targetY || currentHeight >= targetY) {
          el.setAttribute('data-ready', true);
          fetchCategory(id, el);
        }
      });

      {
        const el = carouselRef.current.querySelector('.AllGamesBlock');
        if (!el) return;

        const brect = el.getBoundingClientRect();
        const targetY = brect.y;

        if (currentY >= targetY || currentHeight >= targetY) {
          fetchAllGames();
        }
      }
    };
    document.addEventListener('scroll', handler);
    handler();

    return () => {
      document.removeEventListener('scroll', handler)
    }
  }, []);

  const fetchMoreFeaturedGames = async (limit, index) => {
    const result = await gameApi.listFeaturedOrSticky(limit, index);
	const showNextGames = result.length > 0 ? result : [];
    return showNextGames.filter((game) => game !== undefined);
  };

  const fetchMoreNewGames = async (limit, index) => {
    return await gameApi.listNew(limit, index);
  };

  const fetchMorePopularGames = async (limit, index) => {
    return await gameApi.listPopularAlt(limit, index);
  };

  const fetchMoreAllGames = async (limit, index) => {
    return await gameApi.listAll(limit, index);
  };

  const fetchMoreCategoryGames = async (id, limit, index) => {
    return await gameApi.listByCategory(id, limit, index);
  };

  return (
    <GameTileLayout className={className}>
      <DocumentTitle />

      <Meta>
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'WebSite',
              'name': process.env.APP_CONFIG.DEFAULT_TITLE,
              'url': process.env.APP_CONFIG.URL + '/',
                potentialAction: {
                  '@type': 'SearchAction',
                  'target': `${process.env.APP_CONFIG.SEARCH_URL}{search_term_string}`,
                  'query-input': 'required name=search_term_string'
                }
            }, null, 2)
          }}
        />
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              'name': process.env.APP_CONFIG.ORGANIZATION.NAME,
              'url': process.env.APP_CONFIG.ORGANIZATION.URL,
              'logo': process.env.APP_CONFIG.ORGANIZATION.LOGO,
              'sameAs': [
                ...(process.env.APP_CONFIG.ORGANIZATION.SOCIAL_LINKS || [])
              ]
            }, null, 2)
          }}
        />

        {/*
        remove this from the home page
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@type': 'ItemList',
              'itemListElement': props.popularGames.map((game, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'url': game.path,
                'item': {
                  '@context': 'https://schema.org',
                  '@type': 'VideoGame',
                  'name': game.title,
                  'url': game.path,
                  'image': game.thumbnailUrl,
                  'description': game.descriptionSimple,
                  'inLanguage': [ 'English' ],
                  'aggregateRating':{
                    '@type': 'AggregateRating',
                    'ratingValue': rating(game.rating),
                    'ratingCount': game.totalVotes
                  },
                  'genre': process.env.APP_CONFIG.HOME_PAGE_CATEGORY_CAROUSELS.map((category) => category.name),
                  'gamePlatform': [ 'HTML5' ]
                }
              }))
            }, null, 2)
          }}
        /> */}
      </Meta>

      {/* Featured games */}
      <CarouselBlock featured>
        <Slot name='title'><h2>Featured io Games</h2></Slot>
        <GameCarousel 
          featured
          games={props.featuredGames} 
          stickiedGames={props.stickiedGames}
          fetchMoreGames={fetchMoreFeaturedGames}
          fetchMoreOnLoad
          priority={true}
        />
      </CarouselBlock>
      
      {/* New games */}
      <CarouselBlock>
        <Slot name='title'><h2>New io Games</h2></Slot>
        <GameCarousel 
          games={props.newGames} 
          fetchMoreGames={fetchMoreNewGames}
          fetchMoreOnLoad
          priority={true}
        />
      </CarouselBlock>

      {/* Popular games */}
      <CarouselBlock>
        <Slot name='title'><h2>Popular io Games</h2></Slot>
        <GameCarousel
          games={props.popularGames}
          fetchMoreGames={fetchMorePopularGames}
          fetchMoreOnLoad
          priority={true}
        />
      </CarouselBlock>

      {/* Games of Chance — static cards mirroring iogames-pages /category/casino.
          fetchMoreGames returns the same set on demand so Carousel's pad-to-limit
          loop has non-empty input; GameCarousel.setNextGames dedupes by path so the
          visible row stays at 4 cards. Returning [] would trigger an infinite re-queue. */}
      <CarouselBlock>
        <Slot name='title'><h2>Games of Chance</h2></Slot>
        <GameCarousel
          games={GAMES_OF_CHANCE_CARDS}
          fetchMoreGames={() => GAMES_OF_CHANCE_CARDS}
        />
      </CarouselBlock>

      <div className='Block' ref={carouselRef}>
        {/* Additional categories */}
        {
          For(process.env.APP_CONFIG.HOME_PAGE_CATEGORY_CAROUSELS, (category, index) => (
            <React.Fragment key={category.id}>
              <CarouselBlock
                id={category.id}
                ready={categories[ category.id ] !== undefined}
              >
                <Slot name='title'><h2>{category.name}</h2></Slot>
                {
                  If(categories[ category.id ], () => (
                    <GameCarousel
                      key={`${category.id}_carousel`}
                      games={[]}
                      fetchMoreGames={(nextIndex, limit) => fetchMoreCategoryGames(categories[ category.id ].id, nextIndex, limit)}
                      fetchMoreOnLoad
                    />
                  ))
                    .Else(() => (
                      <GameCarousel
                        key={`${category.id}_skeleton`}
                        games={[]}
                        fetchMoreGames={() => []}
                      />
                    ))
                    .EndIf()
                }
              </CarouselBlock>
              {/* Slot Games — static cards mirroring iogames-pages /category/free-slots,
                  injected directly after the Classic carousel. ready={true} keeps the scroll
                  handler from treating this block as a lazy-fetch target. */}
              {category.name === 'Classic' && (
                <CarouselBlock ready={true}>
                  <Slot name='title'><h2>Slot Games</h2></Slot>
                  <GameCarousel
                    games={SLOT_GAMES_CARDS}
                    fetchMoreGames={() => SLOT_GAMES_CARDS}
                  />
                </CarouselBlock>
              )}
            </React.Fragment>
          ))
        }
      
        {/* All games */}
        {
          If(allGames && allGames.length > 0, () => (
            <AllGamesBlock key='AllGamesBlock' games={allGames} fetchMoreGames={fetchMoreAllGames} center>
              <Slot name='title'>All io Games</Slot>
            </AllGamesBlock>
          ))
            .Else(() => (
              <div key='AllGamesBlock_skeleton' className='AllGamesBlock' />
            ))
            .EndIf()
        }
      </div>

      {
          <Block>
            <Box className='IndexPage__BottomBox'>
              <Title>Discover the Best Free Online Games</Title>
              <Content>
                <p>At iogames.space, we bring you the most exciting <RouterLink href="/popular">io games</RouterLink> available online. Play the newest hits like <RouterLink href="/shell-shockers">Shell Shockers</RouterLink>, <RouterLink href="/baseball-bros">Baseball Bros</RouterLink>, <RouterLink href="/smash-karts-io-game">Smash Karts</RouterLink>, <RouterLink href="/deadshot-io">Deadshot</RouterLink>, <RouterLink href="/hexanautio">Hexanaut</RouterLink>, and <RouterLink href="/powerline-io">Powerline.io</RouterLink> alongside classic favorites such as <RouterLink href="/krunker-io">Krunker.io</RouterLink> and <RouterLink href="/skribbl-io">Skribbl.io</RouterLink>. Our games are sorted by <RouterLink href="/popular">popularity</RouterLink> based on plays from the past week, but you can also browse by <RouterLink href="/popular">top-rated</RouterLink> or <RouterLink href="/new">newest additions</RouterLink>.</p>

                <Title h2 size4>What Are IO Games?</Title>
                <p>IO games represent a unique genre of free online multiplayer games that you can play directly in your browser without downloads or account creation. These games feature simple mechanics that are easy to learn but challenging to master, allowing players to jump right into the action with just one click. Players who enjoy quick browser-based entertainment can also explore <a className='RouterLink' href="https://iogames.space/category/casino">casino games</a> for fast-paced, casual gameplay experiences. The "io" extension originally stands for "Indian Ocean," but game developers favor it because it also represents "input/output" - perfectly describing the accessible nature of these games.</p>

                <Title h2 size4>The Evolution of IO Games</Title>
                <p>The genre began with the wildly addictive <RouterLink href="/category/agario-style"> Agar.io</RouterLink>, which rapidly gained hundreds of thousands of players worldwide shortly after its release. This pioneering game established the framework for future io games by introducing full-screen browser gameplay with entirely multiplayer interaction. Since then, the io gaming landscape has expanded tremendously with creative new concepts and gameplay innovations, including arcade-inspired formats and <a className='RouterLink' href="https://iogames.space/category/free-slots">free slot games</a> that keep browser gaming simple and accessible.</p>

                <Title h2 size4>What Makes a Great IO Game?</Title>
                <p>The most successful io games like <RouterLink href="/category/snake-games">Slither.io</RouterLink>, <RouterLink href="/category/zombies">Zombs.io</RouterLink>, and now <RouterLink href="/shell-shockers">Shell Shockers</RouterLink> and <RouterLink href="/smash-karts-io-game">Smash Karts</RouterLink> share common elements that make them irresistibly playable:</p>
                <ul>
                  <li>Instant accessibility - start playing immediately with no barriers</li>
                  <li>Clean, simple graphics with intuitive controls</li>
                  <li>Progressive scaling that rewards skill and playtime</li>
                  <li>Competitive or cooperative gameplay that keeps you engaged</li>
                  <li>Perfect balance between simplicity and depth</li>
                </ul>
                <p>These characteristics create gaming experiences that appeal to casual players while offering enough complexity to satisfy dedicated gamers. The same focus on simple rules and engaging pacing can be found in modern formats such as <a className='RouterLink' href="https://iogames.space/en/crash-gambling-sites">crash gambling sites</a>, where timing and quick decisions shape the experience.</p>

                <Title h2 size4>Why We Created iogames.space</Title>
                <p>Finding quality io games used to be challenging, with titles scattered across different websites. We established <RouterLink href="/">iogames.space</RouterLink> to create a central hub where players can discover the best free online games in one convenient location. Our mission is to highlight exceptional io games that might otherwise go unnoticed and support the talented developers behind them.</p>
                <p>We update our collection daily with exciting new titles, ensuring you always have fresh gaming experiences to enjoy. From competitive multiplayer arenas to casual picks like <a className='RouterLink' href="https://iogames.space/en/best-online-slots">online slots</a>, our goal is to make discovery quick and enjoyable. Bookmark us (CTRL+D) and check back regularly to discover new free online games that will keep you entertained for hours.</p>
                <p>Whether you're looking for intense action in <RouterLink href="/deadshot-io">Deadshot</RouterLink>, strategic gameplay in <RouterLink href="/hexanautio">Hexanaut</RouterLink>, or multiplayer fun in <RouterLink href="/powerline-io">Powerline.io</RouterLink>, iogames.space has the perfect game for every player. Jump in and start playing today!</p>
              </Content>
            </Box>
          </Block>
      }
    </GameTileLayout>
  )
});
